import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import validate from './OmeMeetsDetailPricingAdditionalPurchasesValidation';

import NavLinks from '../../../meet/NavLinks';
import HostNavLinks from '../../HostNavLinks';
import HostRegistrationConstants from '../HostRegistrationConstants';

import useOmeMeetsDetail from './UseOmeMeetsDetail';

import useOmeMeetData from '../../../../state/omeMeet/UseOmeMeetData';
import useOmeMeetEntryData from '../../../../state/omeMeetEntry/UseOmeMeetEntryData';
import useOmeMeetEntryInvitationsData from '../../../../state/omeMeetEntryInvitations/UseOmeMeetEntryInvitationsData';
import useOmeFeeTypeData from '../../../../state/omeFeeType/UseOmeFeeTypeData';
import useOmeMeetDateValidationData from '../../../../state/omeMeetDateValidation/UseOmeMeetDateValidationData';

import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';

import useForm from '@usas/ui-web/utils/UseForm';

import Constants from '@usas/ui-web/utils/Constants';
import useLeftNavModalData from '@usas/ui-web/state/leftNavModal/UseLeftNavModalData';
import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';
import { getTodaysDate } from '@usas/ui-web/utils/DateFunctions';

const INITIAL_SELECTED_OME_ENTRY_STATE = {
  initialLoadComplete: false,
  selectedOmeMeetEntryId: Constants.DEFAULT_ID
};

const INITIAL_VIEW_STATE = {
  prePutOmeMeetEntryStateObjData: {},
  updatedOmeEntryInvitationArray: [],
  tryPostOmeMeetEntryInvitations: false,
  tryGetOmeMeetEntry: false,
  tryRedirect: false
};

const INITIAL_FORM_STATE = {
  detailFormState: {},
  omeMeetPriceId: Constants.DEFAULT_ID,
  omeFeeTypeId: Constants.DEFAULT_ID,
  omeFeeTypeName: '',
  amount: '',
  description: ''
};

const useOmeMeetsPricingAdditionalPurchases = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedOmeMeetEntryState, setSelectedOmeMeetEntryState] = useState(INITIAL_SELECTED_OME_ENTRY_STATE);
  const [state, setState] = useState(INITIAL_VIEW_STATE);
  const { omeMeetState } = useOmeMeetData();
  const { omeMeetEntryState, getOmeMeetEntry, putOmeMeetEntry } = useOmeMeetEntryData();
  const { omeMeetEntryInvitationsState, postOmeMeetEntryInvitations } = useOmeMeetEntryInvitationsData();
  const { omeMeetDateValidationState, getOmeMeetDateValidation } = useOmeMeetDateValidationData();
  const { omeFeeTypeState } = useOmeFeeTypeData();
  const { leftNavModalState, setLeftNavModalState } = useLeftNavModalData();
  const { formStateHasBeenSaved, editOmeMeetEntryCopy } = useOmeMeetsDetail();
  const { TEAM_OME_FEE_TYPE_ID, CREDENTIAL_OME_FEE_TYPE_ID, INDIVIDUAL_ATHLETE_OME_FEE_TYPE_ID, INDIVIDUAL_INTL_ATHLETE_OME_FEE_TYPE_ID,
    INTL_STAFF_OME_FEE_TYPE_ID, RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID, ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID,
    ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID, RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID } = useEnvironmentVariableData();
  const { formState, errorState, onFormValueChanged, isSubmitting, handleSubmit, onValueTextPairChanged, setIsDirty,
    isDirty, setFormData, setErrors } = useForm(INITIAL_FORM_STATE, submitFormCallback, validate);

  const tryValidateBeforeRedirect = async () => {
    const errors = await validate(formState) || {};
    setErrors(errors);
    if (Object.keys(errors).length === 0) {
      setState({ ...state, tryRedirect: true });
    }
  };

  const onNextButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (isDirty === false) {
      tryValidateBeforeRedirect();
    }
    else {
      handleSubmit();
    }
  };

  const onBackButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (omeMeetState.route !== '') {
      navigate(NavLinks.MEET_HOST_OME_MEETS_DETAIL, { state: { detailFormState: formState.detailFormState, omeMeetEntryId: location.state?.omeMeetEntryId } });
    }
    else {
      navigate(HostNavLinks.OME_MEET_OME_MEETS_DETAIL, { state: { detailFormState: formState.detailFormState, omeMeetEntryId: location.state?.omeMeetEntryId } });
    }
  };

  function submitFormCallback(formState) {
    let updatedOmeMeetEntry = JSON.parse(JSON.stringify(omeMeetEntryState.objData));

    const updatedOmeEntryInvitationArray =
      formState.detailFormState?.orgUnit?.map(x => {
        return (
          {
            orgUnitId: x.id
          }
        )
      });


    if (selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0) {
      //Edit OME Meet
      updatedOmeMeetEntry = editOmeMeetEntryCopy(updatedOmeMeetEntry, formState.detailFormState, selectedOmeMeetEntryState.selectedOmeMeetEntryId);
      const selectedOmeMeetEntryObjIndex = updatedOmeMeetEntry.omeMeetEntry?.findIndex(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);
      //Add Purchase Item
      if (formState.omeMeetPriceId < 0) {
        updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice.push({ omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId, omeFeeTypeId: formState.omeFeeTypeId, amount: formState.amount, description: formState.description.trim() });
      }
      else {
        //Edit Purchase Item
        const selectedOmeFeeTypeIndex = updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex]?.omeMeetPrice.findIndex(x => x.omeMeetPriceId === formState.omeMeetPriceId);
        if (selectedOmeFeeTypeIndex > -1) {
          updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice[selectedOmeFeeTypeIndex].amount = formState.amount;
          updatedOmeMeetEntry.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice[selectedOmeFeeTypeIndex].description = formState.description;
        }
      }

      putOmeMeetEntry(omeMeetState?.objData?.omeMeetId, updatedOmeMeetEntry);

      //Check to see if the invitations are different and need to be saved
      let needToUpdateInvitiations = false;
      const selectedOmeMeetEntryObj = omeMeetEntryState.objData?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);

      if (selectedOmeMeetEntryObj.omeEntryInvitation?.length !== updatedOmeEntryInvitationArray.length) {
        needToUpdateInvitiations = true;
      }
      else {
        for (let i = 0; i < updatedOmeEntryInvitationArray.length; i++) {
          let existingOrgUnitId = selectedOmeMeetEntryObj.omeEntryInvitation?.find(x => x.orgUnitId === updatedOmeEntryInvitationArray[i].orgUnitId);
          if (!existingOrgUnitId) {
            needToUpdateInvitiations = true;
          }
        }
      }

      if (needToUpdateInvitiations === true) {
        setState({ ...state, updatedOmeEntryInvitationArray: updatedOmeEntryInvitationArray, tryPostOmeMeetEntryInvitations: true });
      }
      else {
        setState({ ...state, tryRedirect: true });
      }
    }
    else {
      //Add OME Meet
      const newOmeMeetEntryObj = JSON.parse(JSON.stringify(omeMeetEntryState.objData));
      const todaysDate = getTodaysDate();
      newOmeMeetEntryObj.omeMeetEntry.push({
        omeMeetId: omeMeetEntryState.objData.omeMeetId,
        isMainEntry: false,
        omeMeetName: formState.detailFormState?.omeMeetName.trim() || '',
        meetEntryStartDate: formState.detailFormState?.meetEntryStartDate,
        meetEntryEndDate: formState.detailFormState?.meetEntryEndDate,
        meetEntryChangeDeadline: formState.detailFormState?.meetEntryChangeDeadline,
        meetCreationDate: todaysDate,
        maxNumberOfAthletesForMeet: formState.detailFormState?.maxNumberOfAthletesForMeet.toString().replace(',', '') || null,
        maxNumberOfCapExemptAthletesForMeet: null,
        maxNumberOfAthletesCountsTowardsMainOmeMeet: formState.detailFormState?.maxNumberOfAthletesCountsTowardsMainOmeMeet ?? null,
        includeRelayOnlySwimmersInMaxNumber: formState.detailFormState?.relayEvents === true && formState.detailFormState?.allowRelayOnlySwimmer === true ? (formState.detailFormState?.includeRelayOnlySwimmersInMaxNumber ?? null) : null,
        nationalTeamOnly: formState.detailFormState?.nationalTeamOnly ?? null,
        status: HostRegistrationConstants.COMPLETE_STATUS,
        omeMeetQualificationDate: [{
          omeMeetEntryId: omeMeetEntryState.objData.omeMeetId,
          timeStandardsQualifyingStartDate: formState.detailFormState?.hasTimeStandards === true ? (formState.detailFormState?.hasQualifyingWindow === true ?
            ((formState.detailFormState?.timeStandardsQualifyingStartDate !== Constants.BLANK_DATE_STRING ? formState.detailFormState?.timeStandardsQualifyingStartDate : null)) : Constants.DEFAULT_MIN_DATE)
            : null,
          timeStandardsQualifyingEndDate: formState.detailFormState?.hasTimeStandards === true ? (formState.detailFormState?.hasQualifyingWindow === true ?
            ((formState.detailFormState?.timeStandardsQualifyingEndDate !== Constants.BLANK_DATE_STRING ? formState.detailFormState?.timeStandardsQualifyingEndDate : null)) :
            formState.detailFormState?.meetEntryEndDate.substring(0, 10))
            : null
        }],
        omeEntryInvitation: [],
        omeMeetPrice: [
          { omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId, omeFeeTypeId: formState.omeFeeTypeId, amount: formState.amount, description: formState.description.trim() },
          ...(formState.detailFormState?.costPerTeam !== '' ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: TEAM_OME_FEE_TYPE_ID, amount: formState.detailFormState?.costPerTeam }] : []),
          ...(formState.detailFormState?.costPerCredential !== '' ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: CREDENTIAL_OME_FEE_TYPE_ID, amount: formState.detailFormState?.costPerCredential }] : []),
          ...(formState.detailFormState?.costPerIndividualAthlete !== '' ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: INDIVIDUAL_ATHLETE_OME_FEE_TYPE_ID, amount: formState.detailFormState?.costPerIndividualAthlete }] : []),
          ...(formState.detailFormState?.costPerIndividualInternationalAthlete !== '' && formState.detailFormState?.showCostPerIndividualInternationalAthlete === true ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: INDIVIDUAL_INTL_ATHLETE_OME_FEE_TYPE_ID, amount: formState.detailFormState?.costPerIndividualInternationalAthlete }] : []),
          ...(formState.detailFormState?.costPerInternationalStaff !== '' && formState.detailFormState?.showCostPerInternationalStaff === true ? [{ omeMeetPriceId: undefined, omeFeeTypeId: INTL_STAFF_OME_FEE_TYPE_ID, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, amount: formState.detailFormState?.costPerInternationalStaff }] : []),
          ...(formState.detailFormState?.costPerRelayOnlySwimmer !== '' && formState.detailFormState?.showCostPerRelayOnlySwimmer === true ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID, amount: formState.detailFormState?.costPerRelayOnlySwimmer }] : []),
          ...(formState.detailFormState?.costPerAthleteIndividualEventEntry !== '' ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: ATHLETE_INDIVIDUAL_EVENT_ENTRY_OME_FEE_TYPE_ID, amount: formState.detailFormState?.costPerAthleteIndividualEventEntry }] : []),
          ...(formState.detailFormState?.costPerAthleteIndividualBonusEventEntry !== '' && formState.detailFormState?.showCostPerAthleteIndividualBonusEventEntry === true ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID, amount: formState.detailFormState?.costPerAthleteIndividualBonusEventEntry }] : []),
          ...(formState.detailFormState?.costPerRelayEventEntry !== '' && formState.detailFormState?.showCostPerRelayEventEntry === true ? [{ omeMeetPriceId: undefined, omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0 ? selectedOmeMeetEntryState.selectedOmeMeetEntryId : undefined, omeFeeTypeId: RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID, amount: formState.detailFormState?.costPerRelayEventEntry }] : [])
        ]
      });

      putOmeMeetEntry(omeMeetEntryState.objData.omeMeetId, newOmeMeetEntryObj);
      setState({ ...state, prePutOmeMeetEntryStateObjData: omeMeetEntryState.objData, updatedOmeEntryInvitationArray: updatedOmeEntryInvitationArray, tryPostOmeMeetEntryInvitations: true });
    }
  }

  function additionalPurchaseFormStateHasBeenSaved(formState) {
    //compare formState to omeMeetState to see if formState has been saved
    //Edit Additional Purchase 
    if (formState?.omeMeetPriceId !== Constants.DEFAULT_ID) {
      const selectedOmeMeetEntryObj = omeMeetEntryState.objData?.omeMeetEntry?.find(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);
      const selectedOmeFeeType = selectedOmeMeetEntryObj?.omeMeetPrice?.find(x => x.omeMeetPriceId === formState?.omeMeetPriceId);
      if ((ToIntIfInt(formState?.omeFeeTypeId) !== selectedOmeFeeType?.omeFeeTypeId)
        && (formState?.omeFeeTypeId !== Constants.DEFAULT_ID || selectedOmeFeeType?.omeFeeTypeId !== null)) {
        return false;
      }
      if ((formState?.amount !== selectedOmeFeeType?.amount)
        && (formState?.amount !== '' || selectedOmeFeeType?.amount !== null)) {
        return false;
      }
      if ((formState?.description !== selectedOmeFeeType?.description)
        && (formState?.description !== '' || selectedOmeFeeType?.description !== null)) {
        return false;
      }
    }
    //Add Additional Purchase
    else {
      if (isDirty === true) {
        return false;
      }

      return true;
    }

    return true;
  }

  useEffect(() => {
    if (location.state && location.state?.omeMeetEntryId >= 0) {
      setSelectedOmeMeetEntryState({ ...selectedOmeMeetEntryState, selectedOmeMeetEntryId: location.state.omeMeetEntryId, initialLoadComplete: true });
    }
    else {
      setSelectedOmeMeetEntryState({ ...selectedOmeMeetEntryState, initialLoadComplete: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Object.keys(omeMeetEntryState.objData).length > 0 && omeFeeTypeState.isArrayLoaded === true
      && omeFeeTypeState.isArrayLoading === false && selectedOmeMeetEntryState.initialLoadComplete === true) {
      if (location.state) {
        if (location.state.omeMeetPriceId && selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0) {
          //Edit
          const selectedOmeMeetEntryObjIndex = omeMeetEntryState.objData.omeMeetEntry?.findIndex(x => x.omeMeetEntryId === selectedOmeMeetEntryState.selectedOmeMeetEntryId);
          if (selectedOmeMeetEntryObjIndex > -1) {
            const selectedOmeFeeType = omeMeetEntryState.objData.omeMeetEntry[selectedOmeMeetEntryObjIndex].omeMeetPrice.find(x => x.omeMeetPriceId === location.state.omeMeetPriceId);
            setFormData({
              ...formState,
              detailFormState: location.state.detailFormState || {},
              omeMeetPriceId: location.state.omeMeetPriceId || Constants.DEFAULT_ID,
              omeFeeTypeId: selectedOmeFeeType?.omeFeeTypeId || Constants.DEFAULT_ID,
              omeFeeTypeName: selectedOmeFeeType?.omeFeeType?.typeName || '',
              amount: selectedOmeFeeType?.amount || '',
              description: selectedOmeFeeType?.description || ''
            });
            if (formStateHasBeenSaved(location.state?.detailFormState) === false) {
              setIsDirty(true);
            }
          }
        }
        else {
          //Add
          setFormData({
            ...formState,
            detailFormState: location.state.detailFormState || {}
          });
        }

      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetEntryState.objData, omeFeeTypeState, selectedOmeMeetEntryState]);

  useEffect(() => {
    if (Object.keys(omeMeetState.objData).length > 0) {
      const isSaved = formStateHasBeenSaved(location.state?.detailFormState);
      const isAdditionalPurchaseSaved = additionalPurchaseFormStateHasBeenSaved(formState);
      const isOverallSaved = isSaved === true && isAdditionalPurchaseSaved === true ? true : false;
      setLeftNavModalState({ ...leftNavModalState, formStateSaved: isOverallSaved });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, omeMeetState.objData]);

  useEffect(() => {
    if (omeMeetEntryState.isObjLoading === false && omeMeetEntryState.isSaving === false &&
      omeMeetEntryInvitationsState.isSaving === false && isSubmitting === false &&
      state.tryPostOmeMeetEntryInvitations === true) {
      //Edit
      if (selectedOmeMeetEntryState.selectedOmeMeetEntryId >= 0) {
        postOmeMeetEntryInvitations(selectedOmeMeetEntryState.selectedOmeMeetEntryId, state.updatedOmeEntryInvitationArray);
        setState({ ...state, prePutOmeMeetEntryStateObjData: {}, updatedOmeEntryInvitationArray: [], tryPostOmeMeetEntryInvitations: false, tryGetOmeMeetEntry: true });
      }
      //Add
      else {
        //Find the omeMeetEntryId of the new omeMeetEntry
        let selectedOmeMeetEntryId = Constants.DEFAULT_ID;
        for (let i = 0; i < omeMeetEntryState.objData?.omeMeetEntry?.length; i++) {
          let existingObj = state.prePutOmeMeetEntryStateObjData?.omeMeetEntry?.find(x => x.omeMeetEntryId === omeMeetEntryState.objData.omeMeetEntry[i].omeMeetEntryId)
          if (!existingObj) {
            selectedOmeMeetEntryId = omeMeetEntryState.objData.omeMeetEntry[i].omeMeetEntryId;
          }
        }
        if (selectedOmeMeetEntryId >= 0) {
          postOmeMeetEntryInvitations(selectedOmeMeetEntryId, state.updatedOmeEntryInvitationArray);
        }
        setSelectedOmeMeetEntryState({ ...selectedOmeMeetEntryState, selectedOmeMeetEntryId: selectedOmeMeetEntryId });
        setState({ ...state, prePutOmeMeetEntryStateObjData: {}, updatedOmeEntryInvitationArray: [], tryPostOmeMeetEntryInvitations: false, tryGetOmeMeetEntry: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, omeMeetEntryState.isSaving, omeMeetEntryInvitationsState.isSaving, state]);

  useEffect(() => {
    if (omeMeetEntryState.isObjLoading === false && omeMeetEntryState.isSaving === false &&
      omeMeetEntryInvitationsState.isSaving === false && isSubmitting === false &&
      state.tryGetOmeMeetEntry === true) {
      getOmeMeetEntry(omeMeetEntryState.objData.omeMeetId);
      setState({ ...state, tryGetOmeMeetEntry: false, tryRedirect: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, omeMeetEntryState.isObjLoading, omeMeetEntryState.isSaving, omeMeetEntryInvitationsState.isSaving, state]);

  useEffect(() => {
    if (omeMeetEntryState.isSaving === false && omeMeetEntryInvitationsState.isSaving === false && omeMeetEntryState.isObjLoading === false &&
      isSubmitting === false && state.tryRedirect === true) {
      getOmeMeetDateValidation(omeMeetEntryState.objData.omeMeetId);
      if (omeMeetState.route !== '') {
        navigate(NavLinks.MEET_HOST_OME_MEETS_DETAIL, { state: { omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId } });
      }
      else {
        navigate(HostNavLinks.OME_MEET_OME_MEETS_DETAIL, { state: { omeMeetEntryId: selectedOmeMeetEntryState.selectedOmeMeetEntryId } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetEntryState, omeMeetEntryInvitationsState.isSaving, isSubmitting, state.tryRedirect]);

  return {
    omeMeetEntryState,
    omeMeetEntryInvitationsState,
    omeFeeTypeState,
    omeMeetDateValidationState,
    formState,
    errorState,
    handleSubmit,
    onNextButtonClicked,
    onBackButtonClicked,
    onFormValueChanged,
    onValueTextPairChanged
  };
};

export default useOmeMeetsPricingAdditionalPurchases;